import React, { useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import CalltimeRow from './CalltimeRow';
import IconPicker from './IconPicker';
import RowStyleMenu from './RowStyleMenu';
import AddMenu from './AddMenu';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { GripVertical, Plus, Trash2, X } from 'lucide-react';

const MIN_COL = 8;
const genId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : String(Date.now()) + Math.random().toString(16).slice(2);
const normalizeWidths = (cols) => {
  const sum = cols.reduce((a, c) => a + (c.width || 0), 0) || 1;
  return cols.map((c) => ({ ...c, width: Math.max(MIN_COL, Math.round(((c.width || 0) / sum) * 100)) }));
};

function CalltimeSection({ calltime, onUpdateCalltime, onRemoveCalltime, canRemove }) {
  const cols =
    Array.isArray(calltime.columns) && calltime.columns.length
      ? calltime.columns
      : [
          { id: 'time', label: calltime.headers?.time || 'Time', width: 18 },
          { id: 'name', label: calltime.headers?.name || 'Name', width: 82 },
        ];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: calltime.id, data: { type: 'section' } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const tableRef = useRef(null);

  // ----- Spalten -----
  const setCols = (newCols) => onUpdateCalltime({ ...calltime, columns: newCols });
  const renameColumn = (idx, label) => setCols(cols.map((c, i) => (i === idx ? { ...c, label } : c)));
  const addColumn = () => setCols(normalizeWidths([...cols, { id: genId(), label: 'Neu', width: 12 }]));
  const deleteColumn = (idx) => {
    if (cols.length <= 1) return;
    setCols(normalizeWidths(cols.filter((_, i) => i !== idx)));
  };
  const startResize = (e, idx) => {
    const nextIdx = idx + 1;
    if (nextIdx >= cols.length) return;
    e.preventDefault();
    e.stopPropagation();
    const tableWidth = tableRef.current ? tableRef.current.offsetWidth : 1000;
    const startX = e.clientX;
    const base = cols.map((c) => ({ ...c }));
    const onMove = (ev) => {
      const deltaPct = ((ev.clientX - startX) / tableWidth) * 100;
      let left = (base[idx].width || 0) + deltaPct;
      let right = (base[nextIdx].width || 0) - deltaPct;
      if (left < MIN_COL) { right -= (MIN_COL - left); left = MIN_COL; }
      if (right < MIN_COL) { left -= (MIN_COL - right); right = MIN_COL; }
      setCols(base.map((c, i) => (i === idx ? { ...c, width: Math.round(left) } : i === nextIdx ? { ...c, width: Math.round(right) } : c)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ----- Zeilen -----
  const handleRowDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = calltime.rows.findIndex((row) => row.id === active.id);
      const newIndex = calltime.rows.findIndex((row) => row.id === over.id);
      onUpdateCalltime({ ...calltime, rows: arrayMove(calltime.rows, oldIndex, newIndex) });
    }
  };
  const handleAddRow = (type = 'item') => onUpdateCalltime({ ...calltime, rows: [...calltime.rows, { id: genId(), type, text: '' }] });
  const handleRemoveRow = (rowId) => onUpdateCalltime({ ...calltime, rows: calltime.rows.filter((row) => row.id !== rowId) });
  const handleUpdateRow = (rowId, updatedRow) => onUpdateCalltime({ ...calltime, rows: calltime.rows.map((row) => (row.id === rowId ? updatedRow : row)) });

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, ...(calltime.color ? { borderLeft: `5px solid ${calltime.color}` } : {}) }}
      className="calltime-section mb-8 bg-white rounded-lg shadow-sm border border-slate-200"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-50 border-b border-slate-200 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div
            {...attributes}
            {...listeners}
            className="drag-handle cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 no-print"
            data-testid="calltime-drag-handle"
          >
            <GripVertical className="h-5 w-5" />
          </div>
          <IconPicker
            currentIcon={calltime.icon || 'clock'}
            onSelectIcon={(iconName) => onUpdateCalltime({ ...calltime, icon: iconName })}
          />
          <Input
            type="text"
            value={calltime.title}
            onChange={(e) => onUpdateCalltime({ ...calltime, title: e.target.value })}
            className="font-semibold text-slate-900 w-60 border-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-600"
            placeholder="Calltime Title"
            data-testid="calltime-title-input"
          />
        </div>
        <div className="flex items-center gap-1 no-print">
          <RowStyleMenu showFormat={false} color={calltime.color || null} onChange={(s) => onUpdateCalltime({ ...calltime, color: s.color })} />
          {canRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemoveCalltime}
              className="text-slate-600 hover:text-red-600"
              data-testid="remove-calltime-button"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-visible">
        <table ref={tableRef} className="schedule-table w-full">
          <thead>
            <tr>
              <th className="no-print" style={{ width: '40px' }}></th>
              {cols.map((col, idx) => (
                <th key={col.id} style={{ width: `${col.width}%` }} className="group">
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={col.label}
                      onChange={(e) => renameColumn(idx, e.target.value)}
                      className="font-semibold text-xs bg-transparent border-none w-full focus:outline-none focus:ring-1 focus:ring-blue-600 px-1 rounded"
                      placeholder="Spalte"
                    />
                    {cols.length > 1 && (
                      <button
                        type="button"
                        onClick={() => deleteColumn(idx)}
                        className="no-print opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 shrink-0"
                        title="Spalte löschen"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {idx < cols.length - 1 && (
                    <div
                      className="col-resizer no-print"
                      onMouseDown={(e) => startResize(e, idx)}
                      onClick={(e) => e.stopPropagation()}
                      title="Breite ziehen"
                    />
                  )}
                </th>
              ))}
              <th className="no-print" style={{ width: '88px' }}>
                <button
                  type="button"
                  onClick={addColumn}
                  className="text-slate-400 hover:text-blue-600 flex items-center justify-center w-full"
                  title="Spalte hinzufügen"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRowDragEnd}>
              <SortableContext items={calltime.rows.map((row) => row.id)} strategy={verticalListSortingStrategy}>
                {calltime.rows.map((row) => (
                  <CalltimeRow
                    key={row.id}
                    row={row}
                    columns={cols}
                    onUpdateRow={(updatedRow) => handleUpdateRow(row.id, updatedRow)}
                    onRemoveRow={() => handleRemoveRow(row.id)}
                    canRemove={calltime.rows.length > 1}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </tbody>
        </table>
      </div>

      {/* Add-Menü (+) */}
      <div className="flex gap-2 p-4 no-print">
        <AddMenu
          onAddRow={() => handleAddRow('item')}
          onAddText={() => handleAddRow('text')}
          onAddColumn={addColumn}
        />
      </div>
    </div>
  );
}

export default CalltimeSection;
